import { useMemo, useState } from "react";
import { Box, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Stack, Typography, Chip } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { STATUS_COLORS } from "@/lib/statusColors";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { fetchReport, type ReportResponse, type ReportGroup } from "@/api/reports";

type ViewMode = "day" | "week" | "month";

const AGENT_COLORS: Record<string, string> = {
  alice: "#1e88e5",
  bob: "#9c27b0",
  carol: "#43a047",
  dave: "#fb8c00",
  emma: "#00acc1",
  frank: "#8d6e63",
  grace: "#c0ca33",
  harry: "#5e35b1",
  iris: "#f06292",
  jack: "#00897b",
};

type Segment = { label: string; value: number; color: string };
type Bar = { label: string; total: number; segments: Segment[]; breakdown?: Record<string, number> };

function SimpleStackedBarChart({ data, onSelect }: { data: Bar[]; onSelect?: (b: Bar) => void }) {
  const max = Math.max(...data.map((d) => d.total), 0);
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end", height: 240, px: 1, overflowX: "auto" }}>
      {data.map((bar) => (
        <Box
          key={bar.label}
          sx={{ flex: "0 0 60px", textAlign: "center", cursor: onSelect ? "pointer" : "default" }}
          onClick={() => onSelect?.(bar)}
        >
          <Box
            sx={{
              height: 180,
              display: "flex",
              flexDirection: "column-reverse",
              borderRadius: 1,
              overflow: "hidden",
              border: "1px solid #e0e0e0",
              bgcolor: "#fafafa",
            }}
          >
            {bar.segments.map((seg) => (
              <Box
                key={seg.label}
                sx={{
                  height: max > 0 ? `${(seg.value / max) * 180}px` : 0,
                  bgcolor: seg.color,
                  transition: "height 150ms ease",
                }}
              />
            ))}
          </Box>
          <Typography variant="caption" display="block" noWrap>
            {bar.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {bar.total}
          </Typography>
        </Box>
      ))}
      {max > 0 && (
        <Box sx={{ ml: 1, alignSelf: "flex-start" }}>
          <Typography variant="caption" color="text.secondary">
            Max: {max}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default function ReportsAgent() {
  const [view, setView] = useState<ViewMode>("day");
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [statusFilter, setStatusFilter] = useState<string[]>(["ALL"]);
  const [selectedBar, setSelectedBar] = useState<Bar | null>(null);
  const { user, loading } = useAuth();
  const formattedAnchor = useMemo(() => {
    const d = selectedDate;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, [selectedDate]);

  if (loading) return null;
  if (!user?.is_superuser) return <Navigate to="/search" replace />;

  const canGoForward = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const curr = new Date(selectedDate);
    curr.setHours(0, 0, 0, 0);
    return curr < today;
  }, [selectedDate]);

  const handleDateShift = (delta: number) => {
    const d = new Date(selectedDate);
    if (view === "day") d.setDate(d.getDate() + delta);
    if (view === "week") d.setDate(d.getDate() + delta * 7);
    if (view === "month") d.setMonth(d.getMonth() + delta);
    setSelectedDate(d);
    setSelectedBar(null);
  };

  const reportQuery = useQuery<ReportResponse>({
    queryKey: ["reports-agent", view, formattedAnchor, statusFilter],
    queryFn: () =>
      fetchReport({
        period: view,
        groupBy: view === "day" ? ["agent", "status"] : ["period", "agent"],
        date: formattedAnchor,
        statuses: statusFilter.includes("ALL") ? undefined : statusFilter,
      }),
    refetchOnWindowFocus: false,
  });

  const reportData = reportQuery.data;

  const formattedDate = useMemo(() => {
    const base = new Date(selectedDate);
    if (view === "day") return base.toLocaleDateString();
    if (view === "week") {
      const end = new Date(base);
      end.setDate(end.getDate() + 6);
      return `${base.toLocaleDateString()} – ${end.toLocaleDateString()}`;
    }
    return base.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  }, [selectedDate, view]);

  const bars = useMemo(() => {
    if (!reportData) return [];
    const buckets = reportData.buckets ?? [];

    if (view === "day") {
      const bucket = buckets[0];
      if (!bucket) return [];
      const groups = bucket.groups ?? [];
      return groups.map<Bar>((agentGroup: ReportGroup) => {
        const segments: Segment[] = (agentGroup.subgroups ?? []).map((sub: ReportGroup) => ({
          label: sub.label,
          value: sub.count,
          color: STATUS_COLORS[sub.label] ?? "#90a4ae",
        }));
        return {
          label: agentGroup.label,
          total: agentGroup.count,
          segments,
          breakdown: Object.fromEntries((agentGroup.subgroups ?? []).map((s: ReportGroup) => [s.label, s.count])),
        };
      });
    }

    return buckets.map<Bar>((bucket) => {
      const groups = bucket.groups ?? [];
      const segments: Segment[] = groups.map((g: ReportGroup) => ({
        label: g.label,
        value: g.count,
        color: AGENT_COLORS[g.label] ?? "#90a4ae",
      }));
      const total = segments.reduce((sum, s) => sum + s.value, 0);
      return {
        label: bucket.label,
        total,
        segments,
        breakdown: Object.fromEntries(groups.map((g: ReportGroup) => [g.label, g.count])),
      };
    });
  }, [reportData, view]);

  return (
    <Box sx={{ px: 2, pb: 3 }}>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="flex-end">
          <FormControl size="small">
            <InputLabel id="view-label">View</InputLabel>
            <Select
              labelId="view-label"
              value={view}
              label="View"
              onChange={(e) => setView(e.target.value as ViewMode)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="month">Month</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
          <IconButton onClick={() => handleDateShift(-1)} aria-label="Previous">
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle1">{formattedDate}</Typography>
          <IconButton onClick={() => handleDateShift(1)} aria-label="Next" disabled={!canGoForward}>
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Calendar jump coming later.
        </Typography>
      </Paper>

      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        {view !== "day" && (
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                label="Status"
                multiple
                value={statusFilter}
                onChange={(e) => {
                  const next = e.target.value as string[];
                  setStatusFilter(next.length === 0 ? ["ALL"] : next);
                  setSelectedBar(null);
                }}
                renderValue={(selected) => (selected.includes("ALL") ? "All statuses" : selected.join(", "))}
              >
                <MenuItem value="ALL">All statuses</MenuItem>
                {Object.keys(STATUS_COLORS).map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}

        {reportQuery.isLoading ? (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        ) : bars.length === 0 || bars.every((b) => b.total === 0) ? (
          <Typography variant="body2" color="text.secondary">
            No data to display.
          </Typography>
        ) : (
          <SimpleStackedBarChart data={bars} onSelect={(b) => setSelectedBar(b)} />
        )}

        {selectedBar && selectedBar.breakdown && !(bars.length === 0 || bars.every((b) => b.total === 0)) && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Breakdown for {selectedBar.label}</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
              {view === "day"
                ? Object.entries(selectedBar.breakdown).map(([status, value]) => (
                    <Chip
                      key={status}
                      label={`${status}: ${value}`}
                      sx={{ bgcolor: STATUS_COLORS[status] ?? "grey.200", color: "white" }}
                      size="small"
                    />
                  ))
                : Object.entries(selectedBar.breakdown).map(([agent, value]) => (
                    <Chip
                      key={agent}
                      label={`${agent}: ${value}`}
                      sx={{ bgcolor: AGENT_COLORS[agent] ?? "grey.200", color: "white" }}
                      size="small"
                    />
                  ))}
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
